import { makeVar } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';

import * as graphql from '@treams/treams-network';

/**
 * An account signed in on this browser for the current workspace, as resolved
 * server-side from the workspace's HttpOnly session cookie.
 */
export type SessionSlot = graphql.SessionSlotsQuery['sessionSlots'][number];

/**
 * The user ID this tab expects to act as. Attached to every request as the
 * `X-Treams-User` assertion header; the server rejects requests whose
 * assertion doesn't match the session cookie's active slot, so a stale tab
 * can never silently execute as the wrong user.
 *
 * Written by exactly three flows: the sessionSlots query result (seed),
 * switchSlot, and the mismatch handler in useHttpLink. Never persisted.
 */
export const expectedUserVar = makeVar<string | undefined>(undefined);

/**
 * True while an intentional slot change is in flight: a switch (switchSlot),
 * an impersonation (SupportUserMenu), or a sign-out (Logout). Suppresses the
 * reload-on-mismatch handling in useHttpLink, so responses of requests that
 * raced the slot change don't trigger a spurious hard reload.
 */
export const sessionSlotSwitchInFlight = { current: false };

function purgeLegacyAccounts() {
	try {
		// Session slots used to live in a localStorage 'accounts' key. They are
		// fully server-side now; actively delete the legacy key so it no longer
		// exists for anyone running this version of the software.
		localStorage.removeItem('accounts');
	} catch {
		// localStorage can throw in private mode or when storage is disabled
	}
}

purgeLegacyAccounts();

/**
 * Refetch session slots whenever the tab regains focus (Page Visibility API,
 * with focus/pageshow fallbacks), so an account switch made in another tab is
 * detected the moment the user returns — the refetched response carries the
 * `X-Treams-User` header that triggers the reload-on-mismatch handling.
 *
 * Registered once at module level; the hook keeps the refetch reference
 * pointing at the live query.
 */
let refetchOnFocus: (() => void) | undefined;

if (typeof window !== 'undefined') {
	const handleVisible = () => {
		if (document.visibilityState === 'visible') {
			refetchOnFocus?.();
		}
	};

	document.addEventListener('visibilitychange', handleVisible);
	window.addEventListener('focus', handleVisible);
	window.addEventListener('pageshow', (event) => {
		// bfcache restores don't re-run any of the app's bootstrapping.
		if (event.persisted) {
			refetchOnFocus?.();
		}
	});
}

export interface ISessionSlots {
	/** All accounts signed in on this browser for the current workspace. */
	slots: SessionSlot[];
	/** The active account, i.e. the one requests are executed as. */
	current: SessionSlot | undefined;
	loading: boolean;
	/** Makes another slot the active account (server-side pointer). */
	switchSlot: (userID: string) => Promise<void>;
	refetch: () => Promise<unknown>;
}

export function useSessionSlots(): ISessionSlots {
	const query = graphql.useSessionSlotsQuery();
	const [switchSessionSlot] = graphql.useSwitchSessionSlotMutation();
	const [switchImpersonatedSessionSlot] = graphql.useSwitchImpersonatedSessionSlotMutation();

	const { data, refetch } = query;
	const slots = useMemo(() => data?.sessionSlots ?? [], [data?.sessionSlots]);
	const current = slots.find((slot) => slot.active);

	// Treat "no data yet" as loading even when Apollo isn't fetching (e.g.
	// right after clearStore during an account switch), so gates render a
	// loader instead of flashing the unauthenticated app.
	const loading = query.loading || (!data && !query.error);

	useEffect(() => {
		purgeLegacyAccounts();

		refetchOnFocus = () => {
			refetch();
		};
	}, [refetch]);

	useEffect(() => {
		// Seed only: once a tab has an expectation, changes must go through
		// switchSlot or the reload-on-mismatch handling — never silently.
		if (current && expectedUserVar() === undefined) {
			expectedUserVar(current.userID);
		}
	}, [current]);

	const switchSlot = useCallback(
		async (userID: string) => {
			sessionSlotSwitchInFlight.current = true;

			try {
				const slot = slots.find((s) => s.userID === userID);
				const variables = { input: { userID } };

				// Impersonated slots require support authorization server-side.
				if (slot?.impersonated) {
					await switchImpersonatedSessionSlot({ variables });
				} else {
					await switchSessionSlot({ variables });
				}

				// Update the assertion before anything refetches as the new user.
				expectedUserVar(userID);

				await refetch();
			} finally {
				sessionSlotSwitchInFlight.current = false;
			}
		},
		[slots, switchSessionSlot, switchImpersonatedSessionSlot, refetch],
	);

	return { slots, current, loading, switchSlot, refetch };
}
